import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import ChartCard from './shared/ChartCard';
import ChartPagination from './shared/ChartPagination';
import {
  COLORS, apexAnimation, apexToolbar, apexFontFamily,
  apexLegend, apexGrid, apexTooltipStyle, apexXAxisLabels,
} from './shared/chartTokens';

// ─────────────────────────────────────────────────────────────
// CoverageByModuleChart – Horizontal bar chart for module coverage
// Props:
//   chartData: { series, categories, moduleDetails }
//   loading:   bool
// ─────────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 6;

const SERIES_COLORS = ['#6366F1', '#E0E7FF']; // Current fill, Target fill

const CoverageByModuleChart = ({ chartData = null, loading = false }) => {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { setCurrentPage(1); }, [chartData]);

  // ── Data check ───────────────────────────────────────────────────────────────
  const hasData =
    chartData &&
    chartData.series?.length > 0 &&
    chartData.moduleDetails?.length > 0;

  // ── Pagination ───────────────────────────────────────────────────────────────
  const totalItems  = hasData ? chartData.moduleDetails.length : 0;
  const totalPages  = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex  = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex    = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const pageModules = hasData ? chartData.moduleDetails.slice(startIndex, endIndex) : [];

  const pageCategories    = pageModules.map(m => m.moduleName);
  const pageCurrentData   = pageModules.map(m => m.currentPercentage);
  const pageTargetData    = pageModules.map(m => m.targetPercentage);

  // Dynamic bar height — taller when fewer modules shown
  const barHeightPct = pageModules.length <= 3 ? '40%' : pageModules.length <= 5 ? '55%' : '68%';

  // ── Chart options ────────────────────────────────────────────────────────────
  const chartOptions = {
    chart: {
      type: 'bar',
      height: 'auto',
      toolbar: apexToolbar,
      fontFamily: apexFontFamily,
      background: 'transparent',
      animations: apexAnimation,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: 'end',
        barHeight: barHeightPct,
        dataLabels: { position: 'center' },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontWeight: '700',
        fontFamily: apexFontFamily,
        colors: ['#fff', '#6366F1'],
      },
      formatter: (val, { seriesIndex, dataPointIndex }) => {
        const m = pageModules[dataPointIndex];
        if (!m) return `${val}%`;
        return seriesIndex === 0
          ? `${Number(val).toFixed(1)}% (${m.currentCompleted})`
          : `${Number(val).toFixed(0)}%`;
      },
    },
    stroke: { show: false },
    xaxis: {
      categories: pageCategories,
      labels: {
        ...apexXAxisLabels,
        formatter: (val) => `${val}%`,
      },
      axisBorder: { show: true, color: COLORS.borderLight },
      axisTicks: { show: false },
      min: 0,
      max: 100,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          colors: COLORS.text,
          fontWeight: '500',
          fontFamily: apexFontFamily,
        },
        maxWidth: 200,
      },
    },
    colors: SERIES_COLORS,
    fill: {
      type: ['gradient', 'solid'],
      gradient: {
        type: 'horizontal',
        gradientToColors: ['#8B5CF6'],
        shade: 'light',
        shadeIntensity: 0.3,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100],
      },
      opacity: 1,
    },
    legend: {
      ...apexLegend,
      labels: { colors: [COLORS.text, COLORS.textMuted], useSeriesColors: false },
    },
    grid: {
      ...apexGrid,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    tooltip: {
      shared: false,
      intersect: true,
      style: apexTooltipStyle,
      y: {
        formatter: (val, { seriesIndex, dataPointIndex }) => {
          const m = pageModules[dataPointIndex];
          if (!m) return `${val}%`;
          const label = seriesIndex === 0 ? 'Current Coverage' : 'Target Coverage';
          const cnt   = seriesIndex === 0 ? m.currentCompleted : m.targetCompleted;
          return `${label}: ${Number(val).toFixed(1)}% (${cnt}/${m.totalQuestions} questions)`;
        },
      },
    },
  };

  // Dynamic chart height based on row count
  const chartHeight = Math.max(280, pageModules.length * 64 + 60);

  const badge = hasData ? `${startIndex + 1}–${endIndex} of ${totalItems} modules` : null;

  return (
    <ChartCard
      title="Coverage by Module"
      icon="📋"
      description="Current vs target question coverage per module"
      badge={badge}
      loading={loading}
      isEmpty={!hasData}
      skeletonType="bars"
      skeletonRows={ITEMS_PER_PAGE}
      emptyIcon="📋"
      emptyTitle="No Module Data"
      emptyMessage="Select modules to view coverage comparison."
      accentColor="#8B5CF6"
      minHeight="360px"
    >
      {/* Inner chart bg */}
      <div style={{
        background: '#FAFBFF', borderRadius: '12px',
        border: `1px solid ${COLORS.border}`, padding: '16px 8px', marginBottom: '12px',
        overflowX: 'auto',
      }}>
        <Chart
          options={chartOptions}
          series={[
            { name: 'Current Coverage', data: pageCurrentData },
            { name: 'Target Coverage',  data: pageTargetData  },
          ]}
          type="bar"
          height={chartHeight}
        />
      </div>

      {/* Pagination */}
      <ChartPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPage={setCurrentPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={totalItems}
        itemLabel="modules"
      />
    </ChartCard>
  );
};

export default CoverageByModuleChart;